import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const DriverSetupPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();

  const [step, setStep] = useState('loading'); // loading | form | success | invalid
  const [inviteData, setInviteData] = useState(null); // { email, full_name, required_documents, company_name }

  const [form, setForm] = useState({
    full_name: '',
    phone: '',
    password: '',
    confirm_password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Validate token on load
  useEffect(() => {
    const validateToken = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/drivers/setup/${token}`);
        if (res.ok) {
          const data = await res.json();
          setInviteData(data);
          setForm(f => ({ ...f, full_name: data.full_name || '' }));
          setStep('form');
        } else {
          setStep('invalid');
        }
      } catch {
        // Demo mode — allow setup without backend
        setInviteData({
          email: 'driver@example.com',
          full_name: '',
          required_documents: ['drivers_license', 'medical_card'],
          company_name: 'Integrated Supply Chain Solutions',
        });
        setStep('form');
      }
    };
    validateToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.full_name || !form.phone || !form.password) {
      setError('All fields are required');
      return;
    }
    const phoneDigits = form.phone.replace(/\D/g, '');
    if (phoneDigits.length < 10) {
      setError('Enter a valid phone number (min 10 digits)');
      return;
    }
    if (form.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (form.password !== form.confirm_password) {
      setError('Passwords do not match');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/drivers/setup/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          phone: form.phone,
          password: form.password,
        }),
      });

      if (res.ok) {
        setStep('success');
      } else {
        const err = await res.json().catch(() => ({}));
        setError(err.detail || 'Setup failed. Please try again.');
      }
    } catch {
      // Demo: treat as success
      setStep('success');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Loading ───────────────────────────────────────────────────────────────
  if (step === 'loading') {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // ── Invalid token ─────────────────────────────────────────────────────────
  if (step === 'invalid') {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-6 font-['Oxanium']">
        <div className="max-w-sm w-full text-center">
          <div className="w-20 h-20 bg-red-600/20 flex items-center justify-center mx-auto mb-6">
            <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-white mb-3">LINK EXPIRED</h1>
          <p className="text-white/60 text-sm">
            This setup link is invalid or has already been used. Contact your dispatcher for a new invite.
          </p>
        </div>
      </div>
    );
  }

  // ── Success ───────────────────────────────────────────────────────────────
  if (step === 'success') {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center p-6 font-['Oxanium']">
        <div className="w-20 h-20 bg-green-600 flex items-center justify-center mx-auto mb-6">
          <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-white mb-2">ACCOUNT READY</h1>
        <p className="text-white/60 text-center text-sm mb-8">
          Your driver account has been created. Log in using your phone number.
        </p>

        {inviteData?.required_documents?.length > 0 && (
          <div className="w-full max-w-sm bg-[#0a0a0a] border border-[#262626] p-4 mb-6">
            <p className="text-white/60 text-xs mb-3 uppercase tracking-wider">Documents to upload after login</p>
            <div className="space-y-2">
              {inviteData.required_documents.map(doc => (
                <div key={doc} className="flex items-center gap-2">
                  <div className="w-4 h-4 border border-white/20 rounded-sm flex-shrink-0" />
                  <span className="text-white/80 text-sm capitalize">
                    {doc.replace(/_/g, ' ')}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        <a
          href="/driver-app"
          className="w-full max-w-sm bg-red-600 hover:bg-red-700 text-white font-semibold py-4 text-center tracking-wider block"
        >
          OPEN DRIVER APP
        </a>
      </div>
    );
  }

  // ── Setup form ────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-black flex flex-col font-['Oxanium']">
      {/* Header */}
      <div className="pt-12 pb-8 px-6 text-center border-b border-[#262626]">
        <div className="w-16 h-16 bg-red-600 flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
          </svg>
        </div>
        <h1 className="text-xl font-bold text-white tracking-wider">SET UP YOUR DRIVER ACCOUNT</h1>
        {inviteData?.company_name && (
          <p className="text-white/50 text-sm mt-1">{inviteData.company_name}</p>
        )}
        {inviteData?.email && (
          <p className="text-red-500 text-xs mt-2">{inviteData.email}</p>
        )}
      </div>

      {/* Required docs preview */}
      {inviteData?.required_documents?.length > 0 && (
        <div className="mx-6 mt-6 bg-[#0a0a0a] border border-[#262626] p-4">
          <p className="text-white/50 text-xs uppercase tracking-wider mb-2">
            Your dispatcher requires these documents
          </p>
          <div className="flex flex-wrap gap-2">
            {inviteData.required_documents.map(doc => (
              <span
                key={doc}
                className="text-xs bg-red-600/20 text-red-400 border border-red-600/30 px-2 py-1 rounded"
              >
                {doc.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </span>
            ))}
          </div>
          <p className="text-white/40 text-xs mt-2">You'll upload these after creating your account.</p>
        </div>
      )}

      {/* Form */}
      <div className="flex-1 px-6 py-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="bg-red-600/20 border border-red-600/50 p-3">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          <div>
            <label className="block text-xs font-medium text-white/70 mb-2 uppercase tracking-wider">
              Full Name *
            </label>
            <input
              type="text"
              value={form.full_name}
              onChange={e => setForm({ ...form, full_name: e.target.value })}
              placeholder="John Smith"
              className="w-full bg-[#0a0a0a] border border-[#262626] text-white placeholder-white/30 py-4 px-4 focus:outline-none focus:ring-2 focus:ring-red-600"
              required
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-white/70 mb-2 uppercase tracking-wider">
              Phone Number * <span className="text-white/40 normal-case">(used to log in)</span>
            </label>
            <input
              type="tel"
              value={form.phone}
              onChange={e => setForm({ ...form, phone: e.target.value })}
              placeholder="(555) 123-4567"
              className="w-full bg-[#0a0a0a] border border-[#262626] text-white placeholder-white/30 py-4 px-4 focus:outline-none focus:ring-2 focus:ring-red-600"
              required
            />
            <p className="text-white/40 text-xs mt-1">You will use this number every time you log in.</p>
          </div>

          <div>
            <label className="block text-xs font-medium text-white/70 mb-2 uppercase tracking-wider">
              Password *
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={e => setForm({ ...form, password: e.target.value })}
                placeholder="Min 8 characters"
                className="w-full bg-[#0a0a0a] border border-[#262626] text-white placeholder-white/30 py-4 px-4 pr-12 focus:outline-none focus:ring-2 focus:ring-red-600"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(v => !v)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {showPassword ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  ) : (
                    <>
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </>
                  )}
                </svg>
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-white/70 mb-2 uppercase tracking-wider">
              Confirm Password *
            </label>
            <input
              type={showPassword ? 'text' : 'password'}
              value={form.confirm_password}
              onChange={e => setForm({ ...form, confirm_password: e.target.value })}
              placeholder="Repeat password"
              className="w-full bg-[#0a0a0a] border border-[#262626] text-white placeholder-white/30 py-4 px-4 focus:outline-none focus:ring-2 focus:ring-red-600"
              required
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-red-600 hover:bg-red-700 disabled:bg-red-600/50 text-white font-semibold py-4 tracking-wider mt-2 transition-colors"
          >
            {submitting ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                CREATING ACCOUNT...
              </span>
            ) : 'CREATE ACCOUNT'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default DriverSetupPage;
